import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import {
  computeGranularity,
  toMetricGranularity,
  type DateRangeInput,
  type IngestionFilter,
} from '@/lib/api/system/ingestion-observability';
import {
  FetchMetricQuery,
  MetricCube,
  IngestionMeasure,
  IngestionBreakdown,
} from '@/lib/api/reporting/metrics';
import { useIngestionManifest } from '../hooks/useIngestionManifest';

interface IngestionFailureSummaryProps {
  tenantId: string;
  dateRange: DateRangeInput;
  filters?: IngestionFilter;
}

const STAGE_COLORS = [
  '#ef4444', '#f59e0b', '#f97316', '#ec4899', '#a855f7',
  '#6366f1', '#14b8a6', '#84cc16', '#06b6d4', '#78716c',
];

export function IngestionFailureSummary({ tenantId, dateRange, filters }: IngestionFailureSummaryProps) {
  const { stageDisplayNames } = useIngestionManifest();
  const granularity = computeGranularity(dateRange);

  const { data, isLoading, error } = useQuery(
    FetchMetricQuery({
      tenantId,
      cube: MetricCube.IngestionEvents,
      measure: IngestionMeasure.FailureCount,
      breakdown: IngestionBreakdown.Stage,
      granularity: toMetricGranularity(granularity),
      dateStart: new Date(dateRange.dateStart),
      dateEnd: new Date(dateRange.dateEnd),
      siteId: filters?.projectId,
      cameraId: filters?.videoProcessorConfigId,
    })
  );

  const tickFormat = granularity === 'HOUR'
    ? { month: 'short' as const, day: 'numeric' as const, hour: '2-digit' as const, minute: '2-digit' as const }
    : { month: 'short' as const, day: 'numeric' as const };

  const { chartData, stages } = useMemo(() => {
    if (!data?.data?.length) return { chartData: [], stages: [] };

    const stageTotals: Record<string, number> = {};
    const byBucket: Record<string, Record<string, number>> = {};

    for (const point of data.data) {
      const stage = point.attributes?.['stage'] ?? '';
      const bucket = point.key ?? '';
      const value = point.value ?? 0;

      stageTotals[stage] = (stageTotals[stage] ?? 0) + value;
      if (!byBucket[bucket]) byBucket[bucket] = {};
      byBucket[bucket][stage] = (byBucket[bucket][stage] ?? 0) + value;
    }

    const stages = Object.entries(stageTotals)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([stage]) => stage);

    const chartData = Object.entries(byBucket)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([bucket, counts]) => ({
        bucket: new Date(bucket).toLocaleString(undefined, tickFormat),
        ...counts,
      }));

    return { chartData, stages };
  }, [data, tickFormat]);

  if (isLoading) {
    return (
      <div className="rounded-lg border border-[#2E3835] bg-[#1a1a1a] p-3">
        <Skeleton className="h-[220px] w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-400">
        Failed to load failure summary: {error.message}
      </div>
    );
  }

  if (chartData.length === 0) return null;

  return (
    <div className="rounded-lg border border-[#2E3835] bg-[#1a1a1a]">
      <div className="px-3 pt-3 pb-1">
        <h3 className="text-sm font-medium text-white">Failures by Stage</h3>
        <p className="text-[10px] text-white/40">
          {granularity === 'HOUR' ? 'Hourly' : granularity === 'DAY' ? 'Daily' : 'Weekly'} failure volume per pipeline stage
        </p>
      </div>
      <div className="px-3 pb-3">
        <div className="h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2E3835" vertical={false} />
              <XAxis
                dataKey="bucket"
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 9 }}
                interval="preserveStartEnd"
                axisLine={{ stroke: '#2E3835' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={({ active, label, payload }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <div className="max-w-[300px] rounded-lg border border-[#2E3835] bg-black/95 px-3 py-2 text-xs backdrop-blur-sm">
                      <div className="mb-1 font-medium text-white/70">{label}</div>
                      {payload.map((p) => {
                        const val = (p.value as number) ?? 0;
                        if (val === 0) return null;
                        return (
                          <div key={p.dataKey as string} className="flex items-center gap-1.5 py-0.5">
                            <span
                              className="inline-block h-2 w-2 shrink-0 rounded-sm"
                              style={{ backgroundColor: p.color }}
                            />
                            <span className="truncate text-white/70">{stageDisplayNames.get(p.dataKey as string) ?? (p.dataKey as string)}</span>
                            <span className="ml-auto shrink-0 pl-3 tabular-nums text-white/50">
                              {val.toLocaleString()}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
                cursor={{ stroke: 'rgba(255,255,255,0.1)' }}
              />
              <Legend
                wrapperStyle={{ fontSize: 9, color: 'rgba(255,255,255,0.6)' }}
                formatter={(value: string) => {
                  const display = stageDisplayNames.get(value) ?? value;
                  return (
                    <span className="text-white/60" title={display}>
                      {display.length > 30 ? display.slice(0, 30) + '...' : display}
                    </span>
                  );
                }}
              />
              {stages.map((stage, i) => (
                <Line
                  key={stage}
                  type="monotone"
                  dataKey={stage}
                  stroke={STAGE_COLORS[i % STAGE_COLORS.length]}
                  strokeWidth={1.5}
                  dot={false}
                  name={stageDisplayNames.get(stage) ?? stage}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
